import { SubmitHandler, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ClientCompany, Workstation } from "../types/clientCompanies";
import { EmployeeFormValues } from "../lib/employeeForm";
import {
  employeeLocationTypeOptions,
  formatEmployeeLocationType,
  genderOptions,
  formatGender,
} from "../types/employees";
import Form from "./form/Form";
import FormControl from "./form/FormControl";
import Input from "./form/Input";

type EmployeeFormProps = {
  id: string;
  form: UseFormReturn<EmployeeFormValues>;
  onSubmit: SubmitHandler<EmployeeFormValues>;
  submitError: string | null;
  companies: ClientCompany[];
  workstations: Workstation[];
  isLoadingCompanies: boolean;
  isLoadingWorkstations: boolean;
  disableCompanySelection?: boolean;
  withPlaceholders?: boolean;
  /** Called when the National ID field loses focus, for registry autofill. */
  onNationalIdBlur?: (nationalId: string) => void;
  /** Status/result message from the last registry lookup.  */
  autofillMessage?: string | null;
};

function EmployeeForm({
  id,
  form,
  onSubmit,
  submitError,
  companies,
  workstations,
  isLoadingCompanies,
  isLoadingWorkstations,
  disableCompanySelection = false,
  withPlaceholders = false,
  onNationalIdBlur,
  autofillMessage,
}: EmployeeFormProps) {
  const { t } = useTranslation();
  const companyPlaceholder = isLoadingCompanies
    ? "Loading companies..."
    : "Select client company";
  const selectedCompanyId = form.watch("clientCompanyId");
  const selectedWorkstationId = form.watch("workstationId");
  const selectedLocationType = form.watch("locationType");
  const companyField = form.register("clientCompanyId", {
    required: "Client company is required",
  });
  const workstationField = form.register("workstationId", {
    required: "Workstation is required",
  });
  const nationalIdField = form.register("nationalId", {
    required: "National ID is required",
  });
  const workstationPlaceholder = selectedCompanyId
    ? isLoadingWorkstations
      ? "Loading workstations..."
      : "Select workstation"
    : "Select client company first";

  return (
    <Form id={id} className="authForm" form={form} onSubmit={onSubmit}>
      {submitError ? (
        <p className="errorMessage" role="alert">
          {submitError}
        </p>
      ) : null}

      <div className="formGroup">
        <div className="formGrid2">
          <FormControl<EmployeeFormValues>
            label={t("Full name")}
            name="fullName"
          >
            <Input<EmployeeFormValues>
              name="fullName"
              rules={{ required: "Full name is required" }}
              autoComplete="name"
              placeholder={withPlaceholders ? "Jón Jónsson" : undefined}
            />
          </FormControl>

          <FormControl<EmployeeFormValues>
            label="National ID"
            name="nationalId"
          >
            <input
              className="inputControl"
              {...nationalIdField}
              onBlur={(event) => {
                void nationalIdField.onBlur(event);
                onNationalIdBlur?.(event.target.value);
              }}
              placeholder={withPlaceholders ? "123456-7890" : undefined}
            />
            {autofillMessage ? (
              <span className="hintText" role="status">
                {autofillMessage}
              </span>
            ) : null}
          </FormControl>

          <FormControl<EmployeeFormValues> label={t("Gender")} name="gender">
            <select
              className="inputControl"
              {...form.register("gender")}
              value={form.watch("gender")}
            >
              <option value="">{t("Unspecified")}</option>
              {genderOptions.map((gender) => (
                <option key={gender} value={gender}>
                  {formatGender(gender)}
                </option>
              ))}
            </select>
          </FormControl>

          <FormControl<EmployeeFormValues>
            label={t("Nationality")}
            name="nationality"
          >
            <Input<EmployeeFormValues> name="nationality" />
          </FormControl>

          <FormControl<EmployeeFormValues> label="Email" name="email">
            <Input<EmployeeFormValues>
              name="email"
              type="email"
              autoComplete="email"
              placeholder={withPlaceholders ? "jane.doe@acme.com" : undefined}
            />
          </FormControl>

          <FormControl<EmployeeFormValues> label="Phone" name="phone">
            <Input<EmployeeFormValues>
              name="phone"
              autoComplete="tel"
              placeholder={withPlaceholders ? "+354 555 0000" : undefined}
            />
          </FormControl>

          <FormControl<EmployeeFormValues>
            label={t("Landline")}
            name="landline"
          >
            <Input<EmployeeFormValues> name="landline" autoComplete="tel" />
          </FormControl>
        </div>
      </div>

      <div className="formGroup">
        <div className="formGrid2">
          <FormControl<EmployeeFormValues>
            label="Client Company"
            name="clientCompanyId"
          >
            <select
              className="inputControl"
              {...companyField}
              value={selectedCompanyId}
              onChange={(event) => {
                void companyField.onChange(event);
                if (!disableCompanySelection) {
                  form.setValue("workstationId", "");
                }
              }}
              disabled={isLoadingCompanies || disableCompanySelection}
            >
              <option value="">{companyPlaceholder}</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </FormControl>

          <FormControl<EmployeeFormValues>
            label="Workstation"
            name="workstationId"
          >
            <select
              className="inputControl"
              {...workstationField}
              value={selectedWorkstationId}
              onChange={workstationField.onChange}
              disabled={!selectedCompanyId || isLoadingWorkstations}
            >
              <option value="">{workstationPlaceholder}</option>
              {workstations.map((workstation) => (
                <option key={workstation.id} value={workstation.id}>
                  {workstation.name}
                </option>
              ))}
            </select>
          </FormControl>

          <FormControl<EmployeeFormValues>
            label={t("Department")}
            name="department"
          >
            <Input<EmployeeFormValues> name="department" />
          </FormControl>

          <FormControl<EmployeeFormValues>
            label={t("Job title")}
            name="jobTitle"
          >
            <Input<EmployeeFormValues> name="jobTitle" />
          </FormControl>
          <FormControl<EmployeeFormValues>
            label={t("Location")}
            name="locationType"
          >
            <select
              className="inputControl"
              {...form.register("locationType", {
                required: t("Location is required"),
              })}
              value={selectedLocationType}
            >
              <option value="">{t("Select location")}</option>
              {employeeLocationTypeOptions.map((locationType) => (
                <option key={locationType} value={locationType}>
                  {formatEmployeeLocationType(locationType)}
                </option>
              ))}
            </select>
          </FormControl>
        </div>
      </div>

      <div className="formGroup">
        <FormControl<EmployeeFormValues> label={t("Note")} name="note">
          <textarea
            className="inputControl textareaControl"
            {...form.register("note")}
          />
        </FormControl>
      </div>
    </Form>
  );
}

export default EmployeeForm;
